import { useState } from "react";
import { Flex, Button, ModalV2, Textarea, toast, Text } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import {
  ComplianceCommentFormat,
  ComplianceCommentObjectType,
} from "~/graphql/supergraphEnums";
import { usePerformActionMutation } from "~/graphql/supergraphHooks";
import { type ComplianceCaseAction } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { MarkSuccessKey, getMarkSuccessLabel } from "../utils";

type Props = {
  caseId: number;
  selectValue: string;
  showSubmitModal: boolean;
  onClose: () => void;
  onSubmit: (submitAction: () => void) => void;
};

const AddCommentModal = ({
  caseId,
  selectValue,
  showSubmitModal,
  onClose,
  onSubmit,
}: Props) => {
  const [comment, setComment] = useState("");

  const [performActionMutation, { loading }] = usePerformActionMutation({
    refetchQueries: [
      "GetComplianceCase",
      "GetComplianceCaseAuditLogs",
      "GetComplianceCasesSlice",
    ],
    onCompleted: async () => {
      toast.success(getMarkSuccessLabel(selectValue as MarkSuccessKey));
      await client.refetchQueries({
        include: "active",
      });
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const mutationVariables = {
    caseId: caseId,
    action: selectValue as ComplianceCaseAction,
    ...(comment !== "" && {
      comment: {
        comment: comment,
        format: ComplianceCommentFormat.Text,
        internal: true,
        objectId: caseId,
        objectType: ComplianceCommentObjectType.Case,
      },
    }),
  };

  const submitForReview = () => {
    onSubmit(() => performActionMutation({ variables: mutationVariables }));
    onClose();
  };

  return (
    <ModalV2
      show={showSubmitModal}
      onHide={() => onClose()}
      isCentered={true}
      backdrop={true}
      title="Case resolution note"
    >
      <Textarea
        value={comment}
        placeholder="Case resolution note"
        isFullWidth
        onSetValue={setComment}
      ></Textarea>
      <Text variant="secondary-md">Optional</Text>
      <Flex
        justifyContent="flex-end"
        css={{ marginBottom: SPACING.MEDIUM, marginTop: SPACING.MEDIUM }}
      >
        <Button
          onClick={() => onClose()}
          variant="neutral"
          css={{ marginRight: SPACING.MEDIUM }}
        >
          Cancel
        </Button>
        <Button
          data-testid="resolution-note-button"
          isLoading={loading}
          onClick={submitForReview}
          variant="primary"
        >
          Submit for review
        </Button>
      </Flex>
    </ModalV2>
  );
};

export default AddCommentModal;
